import { useRef } from "react";
import { useResize } from "src/hooks/useResize";
import { useLiveRef } from "src/hooks/useLiveRef";

export const StencilBarSlider = ({
  label,
  value,
  min,
  max,
  onInput,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onInput: (v: number) => void;
  onChange: (v: number) => void;
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const draftRef = useLiveRef(value);

  const compute = (clientX: number) => {
    if (!trackRef.current) return value;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const v = Math.round(min + (max - min) * ratio);
    return Math.max(min, Math.min(max, v));
  };

  const onMouseDown = useResize({
    onDragStart: (ev) => {
      const v = compute(ev.clientX);
      draftRef.current = v;
      onInput(v);
    },
    onDrag: (ev) => {
      const v = compute(ev.clientX);
      draftRef.current = v;
      onInput(v);
    },
    onDragStop: () => onChange(draftRef.current),
  });

  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div className="flex items-center gap-2 h-8 w-full">
      <span className="text-gray-700 whitespace-nowrap">{label}</span>
      <span className="text-gray-700 w-12 text-center bg-gray-50 rounded border border-gray-200 text-xs py-0.5">
        {value} %
      </span>
      <div
        ref={trackRef}
        className="flex-1 h-2 bg-gray-100 rounded cursor-ew-resize relative"
        onMouseDown={onMouseDown}
      >
        <div
          className="absolute inset-y-0 left-0 bg-purple-100 rounded"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
